import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Plus } from 'lucide-react';
import axios from 'axios';
import { useSettings } from '../hooks/useSettings';
import { resolveContentLayout } from '../theme/presets';
import useSubscription from '../hooks/useSubscription';

// Import components
import JobCreateForm from './user/JobCreateForm';
import PagedJobTable from './user/PagedJobTable';
import PageHeader from './user/PageHeader';
import Card from './ui/Card';
import Button from './ui/Button';
import LeadFinderAnnouncementModal from './user/LeadFinderAnnouncementModal';
import LeadFinderUpdateModal, { LEAD_FINDER_UPDATE_VERSION } from './user/LeadFinderUpdateModal';
import { formatApiError } from '../utils/apiError';
import { normalizeApolloUrl } from '../utils/apolloUrl';

const Dashboard = () => {
  const { user, updateUser } = useAuth();
  const { settings, loading: settingsLoading } = useSettings();
  const contentLayout = resolveContentLayout(settings?.content_layout);
  const isFocused = contentLayout === 'focused';

  // Subscription state - using centralized hook
  const { subscription, subscriptionLoading, refetchSubscription } = useSubscription();

  // Check for login-as token in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const loginAsToken = urlParams.get('loginAsToken');
    const userData = urlParams.get('userData');

    if (loginAsToken && userData) {
      sessionStorage.setItem('loginAsToken', loginAsToken);
      sessionStorage.setItem('loginAsUserData', userData);
      try {
        const parsed = JSON.parse(userData);
        if (parsed && typeof parsed === 'object') {
          updateUser(parsed);
        }
      } catch (e) {}
      axios.defaults.headers.common['Authorization'] = `Bearer ${loginAsToken}`;
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    } else {
      const sessionLoginAsToken = sessionStorage.getItem('loginAsToken');
      const sessionUserData = sessionStorage.getItem('loginAsUserData');
      if (sessionLoginAsToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${sessionLoginAsToken}`;
        if (sessionUserData) {
          try {
            const parsed = JSON.parse(sessionUserData);
            if (parsed && typeof parsed === 'object') {
              updateUser(parsed);
            }
          } catch (e) {}
        }
      }
    }
  }, [updateUser]);

  // State management
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    listName: '',
    apolloUrl: '',
    numberOfContacts: ''
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [contactLimits, setContactLimits] = useState({
    min_contacts: 400,
    max_contacts: 50000,
    active_actor: 'code_crafter/apollo-io-scraper'
  });
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [scraperStatus, setScraperStatus] = useState(null);
  const [scraperStatusLoading, setScraperStatusLoading] = useState(true);
  const [systemMessage, setSystemMessage] = useState(null);
  const [systemMessageLoading, setSystemMessageLoading] = useState(true);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // API Functions
  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/api/jobs/`, { timeout: 15000 });
      setJobs(response.data);
      setError('');
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        setError('Request timed out. Please try again.');
      } else if (error.response?.status === 401) {
        setError('Please login again to view your jobs.');
      } else {
        setError('Failed to fetch jobs. Please try refreshing the page.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchContactLimits = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/contact-limits`, { timeout: 5000 });
      setContactLimits(response.data);
    } catch (error) {}
  };

  const fetchScraperStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/settings/scrapers/status`, { timeout: 5000 });
      setScraperStatus(response.data);
      setScraperStatusLoading(false);
    } catch (error) {
      console.error('Error fetching scraper status:', error);
      setScraperStatusLoading(false);
    }
  };

  const fetchSystemMessage = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/jobs/system-message`, { timeout: 5000 });
      setSystemMessage(response.data);
      setSystemMessageLoading(false);
    } catch (error) {
      console.error('Error fetching system message:', error);
      setSystemMessageLoading(false);
    }
  };

  const createJob = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');

    if (!subscription) {
      setError('No active subscription found. Please contact support.');
      setCreating(false);
      return;
    }

    if (subscription.status === 'expired') {
      setError('Your subscription has expired. Please contact support to renew.');
      setCreating(false);
      return;
    }

    const requiredCredits = parseInt(formData.numberOfContacts);
    if (subscription.credits_remaining < requiredCredits) {
      setError(`Insufficient credits. You have ${subscription.credits_remaining} credits remaining, but this job requires ${requiredCredits} credits.`);
      setCreating(false);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/jobs/`, {
        list_name: formData.listName,
        apollo_url: normalizeApolloUrl(formData.apolloUrl),
        number_of_contacts: parseInt(formData.numberOfContacts)
      });
      setCreating(false);
      setFormData({ listName: '', apolloUrl: '', numberOfContacts: '' });
      setShowCreateForm(false);
      setError('');
      setJobs(prevJobs => [response.data, ...prevJobs]);
      refetchSubscription();
    } catch (error) {
      setError(formatApiError(error, 'Failed to create job'));
      setCreating(false);
    }
  };

  // Effects
  useEffect(() => {
    if (!settingsLoading) {
      fetchJobs();
      refetchSubscription();
      fetchContactLimits();
    }
  }, [settingsLoading]);

  const handleCreateJob = () => {
    setShowCreateForm(true);
    setError('');
    fetchContactLimits();
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setFormData({ listName: '', apolloUrl: '', numberOfContacts: '' });
    setError('');
  };

  const sleep = (ms) => new Promise(res => setTimeout(res, ms));
  // Turns a download failure into something true. Previously every outcome -
  // including a permanent 404 and a malformed response the browser refused -
  // showed "Your file is being prepared. Please try again shortly.", so users
  // waited instead of reporting a fault. Only an exhausted 202 poll is
  // genuinely "still preparing".
  const downloadFailureMessage = (failure) => {
    if (!failure) {
      return 'Your file is still being prepared. Please try again in a minute.';
    }
    if (failure.kind === 'network') {
      return `Download failed: ${failure.message}. Please contact support if this keeps happening.`;
    }
    const { status, detail } = failure;
    if (status === 404) return 'This job could not be found. Please refresh the page and try again.';
    if (status === 401 || status === 403) return 'Your session has expired. Please sign in again.';
    if (status === 400) return detail || 'No file is available for this job.';
    if (status >= 500) {
      return `Download failed (server error ${status})${detail ? `: ${detail}` : ''}. Please contact support if it persists.`;
    }
    return `Download failed (error ${status})${detail ? `: ${detail}` : ''}.`;
  };

  const downloadResults = async (jobId, listName) => {
    // Set loading state
    setDownloadingJobId(jobId);

    try {
      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const maxAttempts = 5;
      let attempt = 0;
      let failure = null;

      while (attempt < maxAttempts) {
        try {
          const resp = await axios.get(`${API_BASE_URL}/api/jobs/${jobId}/download`, {
            responseType: 'arraybuffer',
            validateStatus: () => true,
            headers
          });

          if (resp.status === 202) {
            // preparing
            attempt += 1;
            await sleep(3000);
            continue;
          }

          if (resp.status >= 400) {
            // Keep the status: it is the only thing that distinguishes a
            // missing job from an expired session from a server fault, and it
            // used to be discarded here.
            failure = { kind: 'http', status: resp.status };
            try {
              const body = JSON.parse(new TextDecoder('utf-8').decode(resp.data));
              if (body && body.detail) failure.detail = body.detail;
            } catch (_) {
              // Body was not JSON (an HTML error page, or binary). Status alone.
            }
            console.error('Download error', resp.status, failure.detail || '');
            break;
          }

          const contentType = resp.headers['content-type'] || '';
          if (contentType.includes('application/json')) {
            const text = new TextDecoder('utf-8').decode(resp.data);
            const data = JSON.parse(text);
            if (data && data.url) {
              const a = document.createElement('a');
              a.href = data.url;
              a.rel='noopener';
              a.click();
              return;
            }
          }

          // Fallback: treat as excel blob
          const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const safeName = (listName || `apollo_scraping_results_${jobId}`).replace(/[^a-zA-Z0-9]/g, '_');
          link.setAttribute('download', `${safeName}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          return;
        } catch (e) {
          // Includes the case where the browser rejects the response before JS
          // sees it (malformed headers, CORS) - axios reports ERR_NETWORK.
          failure = { kind: 'network', message: (e && e.message) || 'Network error' };
          console.error('Download failed attempt', attempt + 1, e);
          break;
        }
      }

      alert(downloadFailureMessage(failure));
    } finally {
      // Always clear loading state
      setDownloadingJobId(null);
    }
  };

  const handleRefresh = async () => {
    try {
      const refreshTimeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Refresh timed out')), 20000));
      const refreshPromise = Promise.all([ fetchJobs(), refetchSubscription().catch(() => {}) ]);
      await Promise.race([refreshPromise, refreshTimeout]);
    } catch (error) {
      if (error.message === 'Refresh timed out') {
        setError('Refresh timed out. Please try again.');
        setLoading(false);
      }
    }
  };

  // Stripe/Razorpay return toast
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const orderId = params.get('order_id');
    if (checkout === 'success') {
      setTimeout(() => {
        const toast = document.createElement('div');
        toast.style.position = 'fixed';
        toast.style.bottom = '20px';
        toast.style.right = '20px';
        toast.style.padding = '12px 16px';
        toast.style.backgroundColor = 'var(--color-success)';
        toast.style.color = 'white';
        toast.style.borderRadius = '8px';
        toast.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1)';
        toast.style.zIndex = 1000;
        toast.style.display = 'flex';
        toast.style.alignItems = 'center';
        toast.style.gap = '8px';
        toast.textContent = 'Payment successful! Your plan will reflect shortly.';
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 4000);
      }, 100);
      // Clean URL without query params
      window.history.replaceState({}, document.title, window.location.pathname);
      // Try refreshing subscription in background
      refetchSubscription();
    } else if (checkout === 'cancel') {
      setTimeout(() => {
        const toast = document.createElement('div');
        toast.style.position = 'fixed';
        toast.style.bottom = '20px';
        toast.style.right = '20px';
        toast.style.padding = '12px 16px';
        toast.style.backgroundColor = 'var(--color-danger)';
        toast.style.color = 'white';
        toast.style.borderRadius = '8px';
        toast.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1)';
        toast.style.zIndex = 1000;
        toast.style.display = 'flex';
        toast.style.alignItems = 'center';
        toast.style.gap = '8px';
        toast.textContent = 'Payment cancelled. You can try again anytime.';
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 4000);
      }, 100);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Fetch scraper status and system message on mount and poll every 2 minutes
  useEffect(() => {
    fetchScraperStatus();
    fetchSystemMessage();
    const interval = setInterval(() => {
      fetchScraperStatus();
      fetchSystemMessage();
    }, 2 * 60 * 1000); // 2 minutes
    return () => clearInterval(interval);
  }, []);

  // Show Lead Finder announcement modal for users who haven't seen it
  // Skip showing modal during admin impersonation sessions to prevent blocking sidebar
  useEffect(() => {
    const isImpersonated = sessionStorage.getItem('loginAsToken') !== null;
    if (user && user.seen_lead_finder_announcement === false && !isImpersonated) {
      setShowAnnouncementModal(true);
    }
  }, [user]);

  // Handler to dismiss announcement modal
  const handleDismissAnnouncement = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${API_BASE_URL}/api/user/dismiss-announcement`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Update local user state
      if (updateUser && response.data.user) {
        updateUser(response.data.user);
      }

      setShowAnnouncementModal(false);
    } catch (error) {
      console.error('Failed to dismiss announcement:', error);
      // Still close modal even if update fails
      setShowAnnouncementModal(false);
    }
  };

  // Show Lead Finder update modal for users who have submitted at least one Lead Finder job
  // and haven't seen this specific update version
  useEffect(() => {
    const checkLeadFinderUpdate = async () => {
      const isImpersonated = sessionStorage.getItem('loginAsToken') !== null;
      if (isImpersonated) return;

      // Check if user has already seen this update version
      const seenVersion = localStorage.getItem('leadFinderUpdateSeen');
      if (seenVersion === LEAD_FINDER_UPDATE_VERSION) return;

      // Check if user has submitted at least one Lead Finder job
      try {
        const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/api/lead-finder/jobs?limit=1`, {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 5000
        });

        const hasLeadFinderJobs = response.data.jobs && response.data.jobs.length > 0;
        if (hasLeadFinderJobs) {
          setShowUpdateModal(true);
        }
      } catch (error) {
        // Silently fail - don't show modal if we can't check
        console.debug('Could not check Lead Finder jobs for update modal:', error);
      }
    };

    if (user) {
      checkLeadFinderUpdate();
    }
  }, [user, API_BASE_URL]);

  // Handler to dismiss update modal
  const handleDismissUpdate = () => {
    localStorage.setItem('leadFinderUpdateSeen', LEAD_FINDER_UPDATE_VERSION);
    setShowUpdateModal(false);
  };

  // Loading state
  if (settingsLoading) {
    return null;
  }

  return (
    <div>
      {/* Scraper Status Warning Banner */}
      {!scraperStatusLoading && scraperStatus && (
        <>
          {/* Global override warning */}
          {scraperStatus.global_override === 'down' && (
            <div style={{
              padding: '16px',
              marginBottom: '24px',
              backgroundColor: 'var(--color-danger-bg-strong)',
              border: '1px solid #fca5a5',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{
                padding: '8px',
                backgroundColor: 'var(--color-danger-hover)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '32px',
                height: '32px'
              }}>
                <span style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>!</span>
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: 0, marginBottom: '4px', fontSize: '15px', fontWeight: '600', color: 'var(--color-danger-fg)' }}>
                  Service Temporarily Unavailable
                </h4>
                <p style={{ margin: 0, fontSize: '14px', color: '#7f1d1d' }}>
                  {scraperStatus.global_message || 'All scrapers are currently down for maintenance. Please try again later.'}
                </p>
              </div>
            </div>
          )}

          {/* Apollo scraper specific warning */}
          {scraperStatus.global_override !== 'down' && scraperStatus.scrapers?.apollo?.status !== 'up' && (
            <div style={{
              padding: '16px',
              marginBottom: '24px',
              backgroundColor: scraperStatus.scrapers.apollo.status === 'maintenance' ? 'var(--color-warning-bg-strong)' : 'var(--color-danger-bg-strong)',
              border: `1px solid ${scraperStatus.scrapers.apollo.status === 'maintenance' ? '#fcd34d' : '#fca5a5'}`,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{
                padding: '8px',
                backgroundColor: scraperStatus.scrapers.apollo.status === 'maintenance' ? 'var(--color-warning)' : 'var(--color-danger-hover)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '32px',
                height: '32px'
              }}>
                <span style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
                  {scraperStatus.scrapers.apollo.status === 'maintenance' ? '🔧' : '!'}
                </span>
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{
                  margin: 0,
                  marginBottom: '4px',
                  fontSize: '15px',
                  fontWeight: '600',
                  color: scraperStatus.scrapers.apollo.status === 'maintenance' ? 'var(--color-warning-fg)' : 'var(--color-danger-fg)'
                }}>
                  Apollo Scraper {scraperStatus.scrapers.apollo.status === 'maintenance' ? 'Under Maintenance' : 'Unavailable'}
                </h4>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: scraperStatus.scrapers.apollo.status === 'maintenance' ? '#78350f' : '#7f1d1d'
                }}>
                  {scraperStatus.scrapers.apollo.message || `Apollo scraper is currently ${scraperStatus.scrapers.apollo.status}. You cannot create new jobs at this time.`}
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* System Message Banner (Manual Mode Only) */}
      {!systemMessageLoading && systemMessage && systemMessage.has_message && (
        <div style={{
          padding: '16px',
          marginBottom: '24px',
          backgroundColor: isFocused ? 'var(--color-success-bg)' : 'var(--color-info-bg-strong)',
          border: `1px solid ${isFocused ? 'var(--color-success-border)' : '#93c5fd'}`,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <div style={{
            padding: '8px',
            backgroundColor: isFocused ? 'var(--color-success)' : 'var(--color-info)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '32px',
            height: '32px'
          }}>
            <span style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>ℹ</span>
          </div>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: 0, marginBottom: '4px', fontSize: '15px', fontWeight: '600', color: isFocused ? 'var(--color-success-fg)' : 'var(--color-info-fg)' }}>
              System Update
            </h4>
            <p style={{ margin: 0, fontSize: '14px', color: isFocused ? 'var(--color-success-fg)' : '#1e3a8a' }}>
              {systemMessage.message}
            </p>
          </div>
        </div>
      )}

      <PageHeader
        variant={contentLayout}
        title={isFocused ? 'Apollo Scraper' : 'Ready to scrape Apollo.io?'}
        description={isFocused
          ? 'Extract high-quality leads from Apollo search results'
          : 'Create a new scraping job to extract valuable contact data'}
        buttonText="New Scraping Job"
        onButtonClick={handleCreateJob}
        buttonIcon={<Plus size={16} />}
        showButton={!isFocused && !showCreateForm}
        subscription={subscription}
        subscriptionLoading={subscriptionLoading}
      />

      {/* Job creation form. Inline in the focused layout, on demand in classic. */}
      {(isFocused || showCreateForm) && (
        <div style={{ marginBottom: 24 }}>
          <JobCreateForm
            variant={contentLayout}
            formData={formData}
            setFormData={setFormData}
            creating={creating}
            error={error}
            subscription={subscription}
            contactLimits={contactLimits}
            onSubmit={createJob}
            onCancel={isFocused ? undefined : handleCancelCreate}
          />
        </div>
      )}

      {/* Apollo Leads - Paged Recent Requests */}
      <PagedJobTable variant={contentLayout} settings={settings} onDownload={downloadResults} downloadingJobId={downloadingJobId} />

      {/* Lead Finder Announcement Modal */}
      <LeadFinderAnnouncementModal
        isOpen={showAnnouncementModal}
        onClose={handleDismissAnnouncement}
      />

      {/* Lead Finder Update Modal - shows bug fixes to existing users */}
      <LeadFinderUpdateModal
        isOpen={showUpdateModal}
        onClose={handleDismissUpdate}
      />
    </div>
  );
};

export default Dashboard;